import { useEffect } from 'react';
import { makeStyles } from '@material-ui/styles';
import {
    useFieldArray,
    Control,
    UseFormSetError,
    useWatch,
    UseFormClearErrors,
    FormState,
} from 'react-hook-form';
import { parse as parseDate } from 'date-fns';
import { format as formatDate } from 'date-fns-tz';
import cn from 'clsx';
import { Text, ExclamationCircleLine, FieldError } from '@bizzabo/design-system';
import AvailabilitySpansPicker from './AvailabilitySpansPicker';
import { SchedulingPreferencesFormValues } from './useSchedulingPreferencesForm/types';
import { DATE_FORMAT, validateAvailability } from './helpers';
import useIsMobile from '../../../hooks/useIsMobile';

export interface AvailabilityDaysPickerProps {
    control: Control<SchedulingPreferencesFormValues>;
    setError: UseFormSetError<SchedulingPreferencesFormValues>;
    clearErrors: UseFormClearErrors<SchedulingPreferencesFormValues>;
    formState: FormState<SchedulingPreferencesFormValues>;
}

const AvailabilityDaysPicker: React.FC<AvailabilityDaysPickerProps> = ({
    control,
    setError,
    clearErrors,
    formState,
}) => {
    const { fields } = useFieldArray({
        control,
        name: 'availability',
    });

    const availability = useWatch({
        name: 'availability',
        control,
    });

    useEffect(() => {
        clearErrors('availability');
        const errors = validateAvailability(availability);
        errors.forEach(({ field, message }) => {
            setError(field, { message });
        });
    }, [availability]);

    const classes = useStyles();
    const isMobile = useIsMobile();

    return (
        <div>
            {fields.map((item, index) => {
                const errors = formState.errors?.availability?.[index];
                const errorMessage = errors?.date?.message;
                return (
                    <div key={item.id} className={classes.dayRow}>
                        <Text
                            className={cn(classes.dateText, {
                                [classes.noDates]: availability?.[index].intervals.length === 0,
                            })}
                            variant="t80"
                        >
                            {errors && !isMobile ? (
                                <div className={classes.errorTooltip}>
                                    <FieldError
                                        boundariesElement="scrollParent"
                                        error={errorMessage}
                                    />
                                </div>
                            ) : null}
                            {formatDate(
                                parseDate(item.date, DATE_FORMAT, new Date()),
                                'EEE, MMM. d'
                            )}
                            {errors && isMobile ? (
                                <ExclamationCircleLine className={classes.errorIcon} />
                            ) : null}
                        </Text>
                        <AvailabilitySpansPicker nestIndex={index} {...{ control }} />
                        {errors && isMobile ? (
                            <Text className={classes.errorMobileText} variant="t81">
                                {errorMessage}
                            </Text>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
};

const useStyles = makeStyles(
    theme => ({
        dayRow: {
            display: 'flex',
            flexDirection: 'column',
            [theme.breakpoints.up('sm')]: {
                flexDirection: 'row',
            },
        },
        dateText: {
            flex: '0 0 auto',
            alignItems: 'center',
            display: 'flex',
            marginRight: 12,
            height: 36,
            width: '100%',
            [theme.breakpoints.up('sm')]: {
                width: 84,
            },
            position: 'relative',
            overflow: 'visible',
        },
        noDates: {
            color: theme.customization.interactive.G40,
        },
        errorTooltip: {
            position: 'absolute',
            top: -10,
            '& > div': {
                marginLeft: 0,
            },
        },
        errorMobileText: {
            color: theme.palette.primary[500],
        },
        errorIcon: {
            color: theme.palette.primary[500],
            fontSize: 14,
            marginLeft: 6,
        },
    }),
    { classNamePrefix: 'people-user-profile-availability-picker' }
);

export default AvailabilityDaysPicker;
